import { useEffect, useRef, useState } from "react";
import styles from "./InviteAccept.module.css";

/**
 * Thin "open in Atlas Desk" page served at /chat/i/<inviteId>.
 *
 * Earlier versions of this page were a fully-featured invite landing
 * — name input, security-answer field, /verify call, deep link with
 * full peer identity. That logic moved into the chat app itself so
 * the accept UX matches the rest of the product and the web surface
 * stays a thin shim. This page now does two things:
 *
 *   1. Calls /api/invite/<id>/meta to learn the inviter's display
 *      name (so the headline can say "X invited you to chat" instead
 *      of being generic).
 *   2. Hands the invite id off to Atlas Desk via the
 *      `atlas-desk://chat/accept` deep link on click.
 *
 * Stages share the same card:
 *   - "loading" → silent skeleton while /meta is in flight
 *   - "intro"   → "X invited you to chat" + Open CTA + download link
 *   - "opening" → spinner + handoff copy, fires on click + retry
 *   - "stuck"   → "Didn't open?" with Download (primary) + Try again
 *   - "error"   → invite expired / already used / network — no open
 *                 path, only the download fallback
 */
export function InviteAccept({ inviteId }: { inviteId: string }) {
  const [meta, setMeta] = useState<InviteMeta | null>(null);
  const [metaError, setMetaError] = useState<MetaError | null>(null);
  const [stage, setStage] = useState<"opening" | "stuck" | null>(null);
  const stuckTimerRef = useRef<number | null>(null);

  const url = `atlas-desk://chat/accept?inviteId=${encodeURIComponent(inviteId)}`;

  // Fetch invite metadata up front so the page can greet the user by
  // the inviter's name instead of a generic placeholder. Network or
  // 404/409 errors surface a dedicated state — there's no point
  // offering "Open in Atlas Desk" for an invite that no longer
  // exists or has already been consumed.
  useEffect(() => {
    let cancelled = false;
    fetch(`${WORKER_BASE}/api/invite/${encodeURIComponent(inviteId)}/meta`)
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) {
          setMetaError("not_found");
          return;
        }
        if (res.status === 409) {
          setMetaError("already_used");
          return;
        }
        if (!res.ok) {
          setMetaError("unknown");
          return;
        }
        const body = (await res.json()) as InviteMeta;
        setMeta(body);
      })
      .catch(() => {
        if (cancelled) return;
        setMetaError("network");
      });
    return () => {
      cancelled = true;
    };
  }, [inviteId]);

  const attemptOpen = () => {
    setStage("opening");
    let handed = false;
    const markHanded = () => {
      handed = true;
    };
    // Three signals that the OS handed off to Atlas. Whichever fires
    // first wins; subsequent ones are ignored. Cleaned up automatically
    // since each listener is { once: true }.
    window.addEventListener("blur", markHanded, { once: true });
    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.visibilityState === "hidden") markHanded();
      },
      { once: true },
    );
    window.addEventListener("pagehide", markHanded, { once: true });

    window.location.href = url;

    if (stuckTimerRef.current != null) {
      window.clearTimeout(stuckTimerRef.current);
    }
    stuckTimerRef.current = window.setTimeout(() => {
      stuckTimerRef.current = null;
      if (handed) return;
      setStage("stuck");
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (stuckTimerRef.current != null) {
        window.clearTimeout(stuckTimerRef.current);
        stuckTimerRef.current = null;
      }
    };
  }, []);

  return (
    <main className={`${styles.page} atlasDotsBg`}>
      <article className={styles.card}>
        <div className={styles.eyebrow}>Atlas Chat</div>
        {metaError ? (
          <ErrorStage error={metaError} />
        ) : !meta ? (
          <LoadingStage />
        ) : stage === "opening" ? (
          <OpeningStage />
        ) : stage === "stuck" ? (
          <StuckStage onRetry={attemptOpen} />
        ) : (
          <IntroStage meta={meta} onOpen={attemptOpen} />
        )}
      </article>
    </main>
  );
}

/* ---------- stages ---------- */

function LoadingStage() {
  return (
    <>
      <div className={styles.spinner} aria-hidden />
      <h1 className={styles.title}>Loading invite…</h1>
    </>
  );
}

function IntroStage({
  meta,
  onOpen,
}: {
  meta: InviteMeta;
  onOpen: () => void;
}) {
  return (
    <>
      <h1 className={styles.title}>
        {meta.inviter.displayName} invited you to chat
      </h1>
      <button type="button" className={styles.button} onClick={onOpen}>
        Open in Atlas Desk
      </button>
      <a
        className={styles.link}
        href="/#download"
        target="_blank"
        rel="noopener"
      >
        Don't have Atlas Desk yet? Download it
      </a>
    </>
  );
}

function OpeningStage() {
  return (
    <>
      <div className={styles.spinner} aria-hidden />
      <h1 className={styles.title}>Opening Atlas Desk…</h1>
      <p className={styles.body}>
        Handing off your invite. This usually takes a second.
      </p>
    </>
  );
}

function StuckStage({ onRetry }: { onRetry: () => void }) {
  return (
    <>
      <h1 className={styles.title}>Didn't open in Atlas Desk?</h1>
      <p className={styles.body}>
        Your browser may have blocked the hand-off, or Atlas Desk may
        not be installed yet. The invite is still valid — install Atlas
        Desk, then come back to this page and try again.
      </p>
      {/* Download is the primary CTA in this state: "didn't open" most
          often means "not installed," so we make the recovery action
          obvious. Try-again is still available as a secondary path. */}
      <a
        className={styles.button}
        href="/#download"
        target="_blank"
        rel="noopener"
      >
        Download Atlas Desk
      </a>
      <button
        type="button"
        className={styles.buttonSecondary}
        onClick={onRetry}
      >
        Try opening again
      </button>
    </>
  );
}

function ErrorStage({ error }: { error: MetaError }) {
  const { title, body } = errorCopy(error);
  return (
    <>
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.body}>{body}</p>
      <a
        className={styles.link}
        href="/"
        target="_blank"
        rel="noopener"
      >
        Back to atlasdesk.co
      </a>
    </>
  );
}

function errorCopy(error: MetaError): { title: string; body: string } {
  switch (error) {
    case "not_found":
      return {
        title: "Invite expired",
        body: "This invite link has expired or never existed. Ask the sender for a fresh link.",
      };
    case "already_used":
      return {
        title: "Invite already accepted",
        body: "Someone has already accepted this invite. Invites are single-use — ask the sender for a new one if you need to chat.",
      };
    case "network":
      return {
        title: "Couldn't reach the chat server",
        body: "Check your connection and refresh the page.",
      };
    default:
      return {
        title: "Something went wrong",
        body: "Couldn't load this invite. Refresh the page or ask the sender for a fresh link.",
      };
  }
}

/* ---------- types ---------- */

interface InviteMeta {
  inviter: { displayName: string };
  hasSecurityAnswer: boolean;
  securityQuestion?: string;
  suggestedInviteeName?: string;
}

type MetaError = "not_found" | "already_used" | "network" | "unknown";

// Same origin the chat app's `createInvite` POSTs to. Hardcoded
// because the marketing site has no shared config with the chat app
// today — if you move the worker behind a different domain, update
// this constant.
const WORKER_BASE = "https://chat.atlasdesk.co";
